import { location } from '@cwos/api-client';

/**
 * Geofence form logic — pure functions the GeofencesPage and its tests share.
 *
 * Validation mirrors the REAL server schema (`createGeofenceSchema` /
 * `updateGeofenceSchema` in apps/api/src/domains/location/schemas.ts) through
 * the bounds the api-client republishes as `location.GEOFENCE_BOUNDS`: name
 * 1–200 trimmed, latitude −90..90, longitude −180..180, radius a whole number
 * of meters 1..50,000. No invented limits, no silently narrower ones.
 *
 * There is DELIBERATELY no client field on the draft, the create body, or the
 * patch body. work_geofence has no client_id by design (EVV separation —
 * docs/LOCATION-GEOFENCING-DESIGN.md §12): matching a location to a client's
 * home is EVV's future module with its own consent model. The wire-body tests
 * pin the absence.
 */

export interface GeofenceDraft {
  name: string;
  /** Held as text while editing so a half-typed "-8" is not coerced under the cursor. */
  latitude: string;
  longitude: string;
  radiusMeters: string;
  /** Site attachment ('' = none). */
  locationId: string;
  /** Program attachment ('' = none → applies to every program). */
  programId: string;
  status: 'active' | 'inactive';
}

export type GeofenceDraftErrors = Partial<
  Record<'name' | 'latitude' | 'longitude' | 'radiusMeters', string>
>;

export const EMPTY_GEOFENCE_DRAFT: GeofenceDraft = {
  name: '',
  latitude: '',
  longitude: '',
  radiusMeters: '100',
  locationId: '',
  programId: '',
  status: 'active',
};

export function draftOf(geofence: location.Geofence): GeofenceDraft {
  return {
    name: geofence.name,
    latitude: String(geofence.latitude),
    longitude: String(geofence.longitude),
    radiusMeters: String(geofence.radiusMeters),
    locationId: geofence.locationId ?? '',
    programId: geofence.programId ?? '',
    status: geofence.status === 'inactive' ? 'inactive' : 'active',
  };
}

/** A finite decimal within [min, max], else null. */
function boundedNumber(raw: string, bounds: { min: number; max: number }): number | null {
  const trimmed = raw.trim();
  if (trimmed === '' || !/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= bounds.min && n <= bounds.max ? n : null;
}

/** A whole number within [min, max], else null (the schema says `.int()`). */
function boundedInt(raw: string, bounds: { min: number; max: number }): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n >= bounds.min && n <= bounds.max ? n : null;
}

/** The server schema's own refusals, in the page's words. */
export function validateGeofenceDraft(draft: GeofenceDraft): GeofenceDraftErrors {
  const errors: GeofenceDraftErrors = {};
  const name = draft.name.trim();
  if (name.length === 0) {
    errors.name = 'A geofence needs a name.';
  } else if (name.length > location.GEOFENCE_BOUNDS.nameMaxLength) {
    errors.name = 'At most 200 characters.';
  }
  if (boundedNumber(draft.latitude, location.GEOFENCE_BOUNDS.latitude) == null) {
    errors.latitude = 'Between −90 and 90.';
  }
  if (boundedNumber(draft.longitude, location.GEOFENCE_BOUNDS.longitude) == null) {
    errors.longitude = 'Between −180 and 180.';
  }
  if (boundedInt(draft.radiusMeters, location.GEOFENCE_BOUNDS.radiusMeters) == null) {
    errors.radiusMeters = 'Between 1 m and 50 km (50,000 m), in whole meters.';
  }
  return errors;
}

/**
 * The exact `POST /location/geofences` body for a valid draft. Attachments are
 * OMITTED when unset — the create schema has no nullable attachments, and an
 * absent key is the honest "none".
 */
export function createBodyOf(draft: GeofenceDraft): location.GeofenceInput {
  const body: location.GeofenceInput = {
    name: draft.name.trim(),
    latitude: Number(draft.latitude.trim()),
    longitude: Number(draft.longitude.trim()),
    radiusMeters: Number(draft.radiusMeters.trim()),
    status: draft.status,
  };
  if (draft.locationId) body.locationId = draft.locationId;
  if (draft.programId) body.programId = draft.programId;
  return body;
}

/**
 * The exact `PATCH /location/geofences/:id` body: only what changed, with
 * `null` for a DETACHED attachment (the update schema's `nullable()`). Returns
 * null when nothing changed, so the page never sends the server an empty patch
 * it would refuse ("at least one field is required").
 */
export function patchBodyOf(
  saved: location.Geofence,
  draft: GeofenceDraft,
): location.GeofencePatch | null {
  const patch: location.GeofencePatch = {};
  const name = draft.name.trim();
  if (name !== saved.name) patch.name = name;
  if (Number(draft.latitude.trim()) !== saved.latitude) {
    patch.latitude = Number(draft.latitude.trim());
  }
  if (Number(draft.longitude.trim()) !== saved.longitude) {
    patch.longitude = Number(draft.longitude.trim());
  }
  if (Number(draft.radiusMeters.trim()) !== saved.radiusMeters) {
    patch.radiusMeters = Number(draft.radiusMeters.trim());
  }
  if (draft.locationId !== (saved.locationId ?? '')) {
    patch.locationId = draft.locationId === '' ? null : draft.locationId;
  }
  if (draft.programId !== (saved.programId ?? '')) {
    patch.programId = draft.programId === '' ? null : draft.programId;
  }
  if (draft.status !== saved.status) patch.status = draft.status;
  return Object.keys(patch).length > 0 ? patch : null;
}

// --- radius preview arithmetic ---------------------------------------------

/** "750 m" below a kilometer, "1.5 km" at or above — no false precision. */
export function formatMeters(meters: number): string {
  if (meters < 1000) return `${meters} m`;
  const km = meters / 1000;
  const rounded = Math.round(km * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded} km`;
}

/**
 * The scale-bar distance for a radius: the largest 1/2/5 × 10ⁿ meters that is
 * at most the radius, so the bar always fits inside the drawn circle and reads
 * as a round number. The preview's honesty rests on this being real
 * arithmetic, not a decorative ruler.
 */
export function scaleBarMeters(radiusMeters: number): number {
  if (radiusMeters < 1) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(radiusMeters)));
  for (const step of [5, 2, 1]) {
    if (step * magnitude <= radiusMeters) return step * magnitude;
  }
  return magnitude;
}
